import { create } from "zustand";
import faker from "faker";
import { decodeTokenService } from "../services/decodeTokenService";
import { vPagesUsersService } from "../services/vfPages/";
import { WebsiteDeleteResponse } from "../interfaces/websites";
import { Website, WebsitesState } from "../interfaces/websites";
import { WebsiteType, WebsiteStatus } from "../types/websites";
import { getListOfFunnels } from "../services/generativeAI/generativeAIAxios/generateCopysAxios";
import { cachedRequest, clearCachedRequest } from "../utils/requestCache";

const WEBSITES_TTL_MS = 60_000;
let initializePromise: Promise<void> | null = null;
let initializeGeneration = 0;

const fetchWebsites = async (): Promise<Website[]> => {
  // const userEmail = decodeTokenService.getEmailFromToken();
  // if (!userEmail) return []

  const userId = decodeTokenService.getUserIdFromToken() || "";
  const websitesData = await cachedRequest(`websites:${userId}:list`, WEBSITES_TTL_MS, getListOfFunnels);
  const websitesWithUrls = await Promise.all(
    websitesData
      // .filter((site: any) => {
      //   return site.status === "PUBLISH";
      // })
      .map(async (site: any) => {
        try {
          return {
            userId: userId,
            projectId: "",
            websiteId: site.id.toString(),
            name: site.brand_name || site.name || site.product?.name || "",
            productName: site.product?.name || site.name || site.brand_name || "",
            type: (site.type as WebsiteType) || "Landing Page",
            status: "Online" as WebsiteStatus,
            url: site.url || "",
            sales_angle: site.sale_angle?.name || "",
            sales_angle_description: site.sale_angle?.description || site.product?.description || "",
            visits: 0,
            salesSummary: site.sales_summary
              ? {
                salesCount: Number(site.sales_summary.sales_count || 0),
                revenuePaid: site.sales_summary.revenue_paid || 0,
                currencyCode: site.sales_summary.currency_code || undefined,
                conversionRate: site.sales_summary.conversion_rate ?? null,
              }
              : null,
            products: site.products || (site.product?.id ? [{ product_id: site.product.id, type: "PRINCIPAL" }] : []),
            productImage: site.product?.image || "",
            generated: typeof site.sections_generated === "boolean" ? site.sections_generated : undefined,
            generationStatus: site.generation_status ?? null,
            failureStage: site.failure_stage ?? null,
            failureReason: site.failure_reason ?? null,
            failureCode: site.failure_code ?? null,
            retryAvailable: Boolean(site.retry_available),
            recoveryAction: site.recovery_action ?? null,
            conversionRate: 0,
            theme: "Default Theme",
            lastEdited: new Date(site.updated_at || site.created_at).toISOString(),
            createdAt: new Date(site.created_at).toISOString(),
            configurations: site.configurations || { enabled: false, name_funnel: "" },
            idSequence: site.id_sequence_owner_id || 0,
          };
        } catch (error) {
          console.error("Error processing site:", site, error);
          throw error;
        }
      })
  );

  return websitesWithUrls;
};

export const useWebsitesStore = create<WebsitesState>((set) => ({
  projectId: null,
  websites: [],
  tempChanges: {},
  hasChanges: false,
  userId: null,
  isLoading: false,
  loadError: null,

  addWebsite: (website) =>
    set((state) => ({
      websites: [
        ...state.websites,
        {
          ...website,
          id: faker.datatype.uuid(),
          createdAt: new Date().toISOString(),
          lastEdited: new Date().toISOString(),
        },
      ],
      hasChanges: true,
    })),
  deleteWebsite: async (id) => {
    try {
      // Llamada al servicio para eliminar el sitio web
      const userId = decodeTokenService.getUserIdFromToken() || "";

      await vPagesUsersService.deleteWebsite(userId, id);
      // if (response.success) {
      //   set((state) => ({
      //     websites: state.websites.filter(
      //       (website) => website.websiteId !== id
      //     ),
      //     hasChanges: true,
      //   }));
      // } else {
      //   throw new Error("Error al eliminar el sitio web");
      // }
      return "";
    } catch (error) {
      console.error("Error al eliminar el sitio web:", error);
      throw error; // Re-lanzamos el error para manejarlo en el componente
    }
  },
  setTempChanges: (changes) =>
    set(() => ({
      tempChanges: changes,
      hasChanges: Object.keys(changes).length > 0,
    })),
  saveChanges: () =>
    set((state) => {
      const updatedWebsites = state.websites.map((website) => ({
        ...website,
        ...state.tempChanges[website.websiteId],
        lastEdited: new Date().toISOString(),
      }));

      return {
        websites: updatedWebsites,
        tempChanges: {},
        hasChanges: false,
      };
    }),
  discardChanges: () =>
    set({
      websites: [],
      tempChanges: {},
      hasChanges: false,
    }),

  initialize: async () => {
    if (initializePromise) return initializePromise;
    const generation = initializeGeneration;
    set({ isLoading: true, loadError: null });
    let currentPromise: Promise<void> | null = null;
    currentPromise = (async () => {
      try {
        const userId = decodeTokenService.getUserIdFromToken() || "";
        const websites = await fetchWebsites();
        if (generation !== initializeGeneration) return;
        set({
          userId,
          websites,
          isLoading: false,
          loadError: null,
        });
      } catch (error) {
        console.error("Error initializing user:", error);
        if (generation !== initializeGeneration) return;
        set({
          isLoading: false,
          loadError: "No pudimos cargar tus embudos. Revisa la conexión e intenta de nuevo.",
        });
      } finally {
        if (initializePromise === currentPromise) {
          initializePromise = null;
        }
      }
    })();
    initializePromise = currentPromise;
    return initializePromise;
  },

  projectActive: async () => {
    // const userId = await userActive();
    // const userId = decodeTokenService.getUserIdFromToken() || "";
    // if (!userId) throw new Error("Usuario no encontrado");
    // const projectId = await projectActive(userId);
    // if (!projectId) throw new Error("Proyecto no encontrado");
    return "";
  },
}));

export const invalidateWebsitesCache = () => {
  initializeGeneration += 1;
  initializePromise = null;
  clearCachedRequest("websites:");
};
